import React from 'react';
import _ from 'underscore';

import {assertAndContinue} from 'Asserts';
import {SampleDetails} from 'constants/SampleConstants';
import {RegistrationTable} from 'notebook/registration-tables/RegistrationTableConstants';
import * as ExcessUtil from 'notebook/tables/excess/Util';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/TableConstants';
import {formatTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {StageRunTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';
import {
  TransferOperation,
  TransferOperationList,
  TransferTableModel,
} from 'notebook/transfer-tables/TransferTableConstants';
import BioentityChip from 'ui-library/chips/BioentityChip';
import {ChipSize} from 'ui-library/chips/Chip';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import pick from 'util/pick';
import {areSimilar} from 'workflows/util/StageRunSampleUtil';
import {stringifyStructuredFieldDefinition} from 'workflows/util/StructuredDataUtil';
import {
  STAGE_RUN_SAMPLE_SUMMARY_SHAPE,
  STAGE_RUN_SUMMARY_SHAPE,
  WorkflowStructuredData,
} from 'workflows/WorkflowConstants';
import {
  RegistrationRecordList,
  StageRun,
  StageRunSample,
  StageRunSampleDetails,
  StageRunSampleKey,
} from 'workflows/WorkflowConstants';

export interface SampleKeysByTableName {
  [tableName: string]: Array<StageRunSampleKey>;
}

export interface SampleDetailsByTableName {
  [tableName: string]: Array<StageRunSampleDetails>;
}

export type SampleDetailsWithOptionalStageRunSample = SampleDetails & {
  stageRun?: StageRun;
  stageRunSample?: StageRunSample;
};

export type SampleDetailsWithStageRunSample = SampleDetails & StageRunSampleDetails;

export interface StageRunParentInfo {
  parentStageRun: StageRun;
  stageRunSampleById: {[id: string]: StageRunSample};
}

function sampleKeyStr(sample: StageRunSampleKey): string {
  return `${sample.stageRunId},${sample.stageRunSampleId}`;
}

export function subtractSampleKeyLists(
  list1: Array<StageRunSampleKey>,
  list2: Array<StageRunSampleKey>
): Array<StageRunSampleKey> {
  const list2Strs = new Set(list2.map((sample) => sampleKeyStr(sample)));
  return list1.filter((sampleKey) => !list2Strs.has(sampleKeyStr(sampleKey)));
}

/**
 * Return an array of {stageRunId, stageRunSampleId} objects representing all stage run inputs.
 */
export function getInputSampleKeys(stageRuns: Array<StageRun>): Array<StageRunSampleKey> {
  const sampleKeys = [];
  for (const stageRun of stageRuns) {
    for (const stageRunSampleId of stageRun.inputSampleIds) {
      sampleKeys.push({stageRunId: stageRun.id, stageRunSampleId});
    }
  }
  return sampleKeys;
}

/**
 * Return an array of {stageRunId, stageRunSampleId} objects representing all stage run outputs.
 */
export function getOutputSampleKeys(stageRuns: Array<StageRun>): Array<StageRunSampleKey> {
  const sampleKeys = [];
  for (const stageRun of stageRuns) {
    for (const stageRunSampleId of stageRun.outputSampleIds) {
      sampleKeys.push({stageRunId: stageRun.id, stageRunSampleId});
    }
  }
  return sampleKeys;
}

export function getRegisteredSampleKeysFromListId(
  registrationRecordListId: string | null,
  registrationRecordListsById: {[id: string]: RegistrationRecordList}
): Array<StageRunSampleKey> {
  const sampleKeys: Array<StageRunSampleKey> = [];
  const registrationRecordList = registrationRecordListsById[registrationRecordListId!];
  if (!registrationRecordList) {
    return sampleKeys;
  }
  for (const record of registrationRecordList.registrationRecords) {
    sampleKeys.push({
      stageRunId: record.stageRunId,
      stageRunSampleId: record.stageRunSampleId,
    });
  }
  return sampleKeys;
}

export function getTransferredSampleKeysFromListId(
  transferOperationListId: string,
  transferOperationListsById: {[id: string]: TransferOperationList},
  transferOperationsById: {[id: string]: TransferOperation}
): Array<StageRunSampleKey> {
  const sampleKeys: Array<StageRunSampleKey> = [];
  const transferOperationList = transferOperationListsById[transferOperationListId];
  if (!transferOperationList) {
    return sampleKeys;
  }
  for (const transferOperationId of transferOperationList.transferOperationIds) {
    const transferOperation = transferOperationsById[transferOperationId];
    if (!transferOperation) {
      continue;
    }
    for (const record of transferOperation.transferRecords) {
      sampleKeys.push({
        stageRunId: transferOperation.stageRunId,
        stageRunSampleId: record.outputStageRunSampleId,
      });
    }
  }
  return sampleKeys;
}

export function getTableIconsByTableName({
  registrationTables,
  transferTables,
}: {
  registrationTables: Array<RegistrationTable>;
  transferTables?: Array<TransferTableModel>;
}): {[tableName: string]: BenchlingIconName} {
  const icons: {[tableName: string]: BenchlingIconName} = {Input: 'input'};
  if (registrationTables) {
    for (const registrationTable of registrationTables) {
      icons[registrationTable.name] = 'register';
    }
  }
  if (transferTables) {
    for (const transferTable of transferTables) {
      icons[transferTable.name] = 'transfer';
    }
  }
  return icons;
}

function getRegisteredSampleKeysByTableName(
  registrationTables: Array<RegistrationTable>,
  registrationRecordListsById: {[id: string]: RegistrationRecordList},
  tagSchemaIds: Set<string>
): {[tableName: string]: Array<StageRunSampleKey>} {
  const registeredSampleKeys: {[tableName: string]: Array<StageRunSampleKey>} = {};
  for (const registrationTable of registrationTables) {
    if (tagSchemaIds.has(registrationTable.tagSchemaId)) {
      const tableName = registrationTable.name;
      const tableKeys = getRegisteredSampleKeysFromListId(
        registrationTable.registrationRecordListId,
        registrationRecordListsById
      );
      if (!registeredSampleKeys[tableName]) {
        registeredSampleKeys[tableName] = [];
      }
      registeredSampleKeys[tableName] = [...registeredSampleKeys[tableName], ...tableKeys];
    }
  }
  return registeredSampleKeys;
}

function getTransferredSampleKeysByTableName(
  transferTables: Array<TransferTableModel>,
  transferOperationListsById: {[id: string]: TransferOperationList},
  transferOperationsById: {[id: string]: TransferOperation}
): {[tableName: string]: Array<StageRunSampleKey>} {
  const transferredSampleKeys: {[tableName: string]: Array<StageRunSampleKey>} = {};
  for (const transferTable of transferTables) {
    const tableName = transferTable.name;
    const tableKeys = getTransferredSampleKeysFromListId(
      transferTable.transferOperationListId,
      transferOperationListsById,
      transferOperationsById
    );
    if (!transferredSampleKeys[tableName]) {
      transferredSampleKeys[tableName] = [];
    }
    transferredSampleKeys[tableName] = [...transferredSampleKeys[tableName], ...tableKeys];
  }
  return transferredSampleKeys;
}

/**
 * Return an object of arrays of {stageRunId, stageRunSampleId} objects, keyed on the source table,
 * representing all samples available for adding to another table.
 */
export function getAvailableSampleKeysByTableName({
  stageRuns,
  registrationTables,
  registrationRecordListsById,
  tagSchemaIds,
  transferTables,
  transferOperationListsById,
  transferOperationsById,
}: {
  stageRuns: Array<StageRun>;
  registrationTables: Array<RegistrationTable>;
  registrationRecordListsById: {[id: string]: RegistrationRecordList};
  tagSchemaIds: Set<string>;
  transferTables?: Array<TransferTableModel>;
  transferOperationListsById?: {[id: string]: TransferOperationList};
  transferOperationsById?: {[id: string]: TransferOperation};
}): {[tableName: string]: Array<StageRunSampleKey>} {
  let availableSampleKeys = {Input: getInputSampleKeys(stageRuns)};
  if (registrationTables) {
    availableSampleKeys = {
      ...availableSampleKeys,
      ...getRegisteredSampleKeysByTableName(registrationTables, registrationRecordListsById, tagSchemaIds),
    };
  }
  if (transferTables) {
    availableSampleKeys = {
      ...availableSampleKeys,
      ...getTransferredSampleKeysByTableName(
        transferTables,
        transferOperationListsById!,
        transferOperationsById!
      ),
    };
  }

  return availableSampleKeys;
}

/**
 * Given an array of {stageRunId, stageRunSampleId} objects, return an array of
 * {stageRun, stageRunSample} objects ordered properly by stage run.
 *
 * If we don't have a value for a stage run sample, skip that row.
 */
export function resolveSampleKeyList(
  sampleKeys: Array<StageRunSampleKey>,
  stageRuns: Array<StageRun>,
  stageRunSampleById: {[id: string]: StageRunSample}
): Array<StageRunSampleDetails> {
  const sampleKeysByStageRunId = _.groupBy(sampleKeys, 'stageRunId');
  const results = [];
  for (const stageRun of stageRuns) {
    for (const sampleKey of sampleKeysByStageRunId[stageRun.id] || []) {
      const stageRunSample = stageRunSampleById[sampleKey.stageRunSampleId];
      if (stageRunSample) {
        results.push({stageRun, stageRunSample});
      }
    }
  }
  return results;
}

export function resolveSampleKeysByTableName(
  sampleKeys: SampleKeysByTableName,
  stageRuns: Array<StageRun>,
  stageRunSampleById: {[id: string]: StageRunSample}
): SampleDetailsByTableName {
  const results: SampleDetailsByTableName = {};
  for (const tableName of Object.keys(sampleKeys)) {
    results[tableName] = resolveSampleKeyList(sampleKeys[tableName], stageRuns, stageRunSampleById);
  }
  return results;
}

const StageRunSampleChip = ({sample}: {sample: StageRunSample}): JSX.Element => {
  if (sample.fileId != null) {
    return <BioentityChip id={sample.fileId} size={ChipSize.TABLE_CELL} />;
  }
  if (sample.batch != null) {
    return <span>{sample.batch.id}</span>;
  }
  if (sample.name != null) {
    return <span>{sample.name}</span>;
  }

  return <span>Loading</span>;
};
StageRunSampleChip.propTypes = {
  sample: STAGE_RUN_SAMPLE_SUMMARY_SHAPE.isRequired,
};

// stageRun can be null if the stage run has just been removed from the entry and the entry is being
// refreshed, or if looking at an old version where stage runs have been deleted.
export function StageRunSampleRowLabel({
  stageRun,
  stageRunSample,
}: {
  stageRun: StageRun | null;
  stageRunSample: StageRunSample | null;
}): JSX.Element {
  if (stageRunSample == null) {
    return <span>Loading</span>;
  }
  return (
    <span>
      <StageRunSampleChip sample={stageRunSample} /> {stageRun ? `(${stageRun.name})` : null}
    </span>
  );
}
StageRunSampleRowLabel.propTypes = {
  stageRun: STAGE_RUN_SUMMARY_SHAPE,
  stageRunSample: STAGE_RUN_SAMPLE_SUMMARY_SHAPE,
};

export function getFieldDefinitions(structuredData: object | null): Array<{tableName: string; name: string}> {
  if (!structuredData) {
    return [];
  }

  return _.chain(structuredData)
    .pluck('fields')
    .flatten()
    .uniq(false /* isSorted */, stringifyStructuredFieldDefinition)
    .map((field) => pick(field, 'tableName', 'name'))
    .value();
}

/**
 * Extract the field values from the structured data for a given stage run.
 *
 * If `stageRunSampleId` is specified, only data related to that sample is included,
 * otherwise the data for all stage run samples is included.
 */

export function getFieldValueCells(
  structuredData: WorkflowStructuredData | null,
  stageRunId: string,
  {stageRunSampleId}: {stageRunSampleId?: string} = {}
): Array<{tableName: string; name: string}> {
  if (!structuredData) {
    return [];
  }
  return _.chain(structuredData)
    .filter(
      (data) =>
        data.stageRunId === stageRunId &&
        (stageRunSampleId == null || data.stageRunSampleId === stageRunSampleId)
    )
    .pluck('fields')
    .flatten()
    .value();
}

/**
 * Return a list of stage runs representing the parents of the specified stageRun.
 *
 * If there is no stage run found for at least one of the parents null is returned for the
 * corresponding parentId.
 */
export function getParentStageRuns(stageRun: StageRun, stageRuns: Array<StageRun>): Array<StageRun | null> {
  return stageRun.stageFlowNode.parentIds.map((parentStageFlowNodeId) => {
    const matchingStageRuns = stageRuns.filter(
      (possibleParent) => possibleParent.stageFlowNode.id === parentStageFlowNodeId
    );
    assertAndContinue(
      matchingStageRuns.length <= 1,
      `Found multiple stage runs matching stage flow node ${parentStageFlowNodeId}.`
    );
    if (matchingStageRuns.length !== 1) {
      return null;
    }
    return matchingStageRuns[0];
  });
}

/**
 * Can only be called if stageRun has a parent.
 * Return all (loaded) parent output samples that are not already the input to this stage run.
 */
export function getOtherParentOutputSamples(
  stageRun: StageRun,
  {parentStageRun, stageRunSampleById}: StageRunParentInfo
): Array<StageRunSample> {
  const inputSamples = _.compact(stageRun.inputSampleIds.map((sampleId) => stageRunSampleById[sampleId]));
  const outputSamples = _.compact(
    parentStageRun.outputSampleIds.map((sampleId) => stageRunSampleById[sampleId])
  );
  return outputSamples.filter(
    (sample) => !inputSamples.some((inputSample) => areSimilar(inputSample, sample))
  );
}

/**
 * Given a typedValue and an array of available stage runs, resolve to a stage run typedValue or
 * give an error.
 */
export function resolveStageRunValue(
  typedValue: TypedValue,
  stageRuns: Array<StageRun>
): {resolvedValue: TypedValue; error: ExcessUtil.ValueError | null} {
  const text = formatTypedValue(typedValue);
  const stageRun = stageRuns.find((run) => normalizeName(run.name) === normalizeName(text));
  if (!stageRun) {
    const message = text === '' ? 'Stage Run is required.' : `Invalid stage run name: ${text}`;
    return {resolvedValue: typedValue, error: new ExcessUtil.ValueError(message)};
  } else {
    return {
      resolvedValue: stageRunTypedValue(stageRun),
      error: null,
    };
  }
}

function normalizeName(name: string): string {
  return name.toLowerCase().trim();
}

export function stageRunTypedValue(stageRun: StageRun): StageRunTypedValue {
  return {
    type: BaseTypedValueType.STAGE_RUN,
    stageRunId: stageRun.id,
    stageRunName: stageRun.name,
  };
}
